import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { getObjectId } from '@/helpers/object.id.helper';

export const GET_EVENT_QUERY_KEY = 'get-event';

export const normalizeEventResponse = (data: EventDto | EventDto[] | undefined, requestedId?: string) => {
  const events = Array.isArray(data) ? data : data ? [data] : [];
  const eventPlan = events.find((event) => event.mode === 'PLAN');
  const eventOrganize = events.find((event) => event.mode === 'ORGANIZE');

  let selectedEvent = events.find((event) => event.id === requestedId);
  if (!selectedEvent && requestedId) {
    selectedEvent = events.find((event) => {
      const originId = typeof event.origin === 'string' ? event.origin : getObjectId(event.origin);
      return originId === requestedId;
    });
  }
  if (!selectedEvent) {
    selectedEvent = eventPlan || eventOrganize || events[0];
  }

  return { events, eventPlan, eventOrganize, selectedEvent };
};

// Query event by originId - backend decides what mode documents to return
export const useGetEventQuery = (id?: string) => {
  const { getToken } = useAuth();

  const query = useQuery({
    queryKey: [GET_EVENT_QUERY_KEY, id],
    queryFn: async () => {
      const token = await getToken();
      return getEvent({ id: id!, token: token ?? '' });
    },
    enabled: !!id,
  });

  const normalized = useMemo(() => normalizeEventResponse(query.data, id), [query.data, id]);

  return {
    ...query,
    data: normalized.selectedEvent,
    normalized,
  };
};

const getEventByMode = async ({
  id,
  token,
  mode,
}: {
  id: string;
  token: string;
  mode: 'PLAN' | 'ORGANIZE';
}): Promise<EventDto> => {
  const url = new URL(`${import.meta.env.VITE_API_URL}/events/${id}`);
  url.searchParams.set('mode', mode);
  return sendJSONRequest({ url: url.toString(), method: METHOD.GET, token });
};

const getEvent = async ({ id, token }: { id: string; token: string }): Promise<EventDto | EventDto[]> => {
  const results = await Promise.allSettled([
    getEventByMode({ id, token, mode: 'PLAN' }),
    getEventByMode({ id, token, mode: 'ORGANIZE' }),
  ]);

  const events = results
    .filter((result): result is PromiseFulfilledResult<EventDto> => result.status === 'fulfilled')
    .map((result) => result.value);

  if (events.length === 0) {
    const firstRejection = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (firstRejection) {
      throw firstRejection.reason;
    }
    throw new Error('Event not found');
  }

  return events.length === 1 ? events[0] : events;
};
